/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, ElementRef, Renderer2, signal, ViewChild} from '@angular/core';

@Component({
    selector: 'app-tooltip-image',
    template: `
        @if (visible() && imageUrl) {
          <div class="grid-tooltip" [style.left.px]="x" [style.top.px]="y"
            (mouseenter)="enterTooltip()" (mouseleave)="exitTooltip($event)">
            <img [src]="imageUrl" alt="Tooltip image" #tooltipImage (load)="onImageLoad()">
          </div>
        }
        `,
    styles: [`
      .grid-tooltip {
        position: fixed;
        z-index: 1200;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: white;
      }
      .grid-tooltip img {
        border: 5px solid black;
        border-radius: 5px;
        box-sizing: border-box; /* Include the border in size calculations */
      }
    `],
    standalone: false
})
export class TooltipImageComponent {
    readonly visible = computed(() => this.isOriginalImageHovered() || this.isPopupHovered());
    x = 0;
    y = 0;
    imageUrl = ''; // URL of the image to display in the tooltip
    private readonly isOriginalImageHovered = signal(false);
    private readonly isPopupHovered = signal(false);
    private originalImageElement: HTMLElement | null = null;

    @ViewChild('tooltipImage', { static: false }) tooltipImage!: ElementRef<HTMLImageElement>;

    constructor(public el: ElementRef, private renderer: Renderer2) {}

    show(imageUrl: string, x: number, y: number, originalImageElement?: HTMLElement): void {
        this.imageUrl = imageUrl;
        this.x = x;
        this.y = y - 15;
        this.originalImageElement = originalImageElement ?? this.originalImageElement;
        this.isOriginalImageHovered.set(true);
    }

    // Called when the image has finished loading
    onImageLoad(): void {
        const imageElement = this.tooltipImage.nativeElement;

        // Apply a 2x zoom factor to the image
        const naturalWidth = imageElement.naturalWidth * 2;
        const naturalHeight = imageElement.naturalHeight * 2;

        // Constrain dimensions to 600x400, while maintaining aspect ratio
        const ratio = Math.min(600 / naturalWidth, 400 / naturalHeight, 1);
        const adjustedWidth = Math.round(naturalWidth * ratio);
        const adjustedHeight = Math.round(naturalHeight * ratio);

        // Dynamically set the image size
        this.renderer.setStyle(imageElement, 'width', `${adjustedWidth}px`);
        this.renderer.setStyle(imageElement, 'height', `${adjustedHeight}px`);

        // Get screen space available
        const spaceRight = window.innerWidth - this.x;
        const spaceLeft = this.x;
        const spaceBottom = window.innerHeight - this.y;
        const spaceTop = this.y;

        // If the image doesn't fit to the right, open to the left
        if (adjustedWidth > spaceRight && spaceLeft >= spaceRight) {
            this.x -= (adjustedWidth - 10); // Open to the left, subtracting 10 because of the 5px border
        }
        // If the image doesn't fit below, open above
        if (adjustedHeight > spaceBottom && spaceTop >= spaceBottom) {
            this.y -= (adjustedHeight - 10); // Open above, subtracting 10 because of the 5px border
        }
    }

    leaveOriginalImage(relatedTarget: EventTarget | null): void {
        this.isOriginalImageHovered.set(false);
        this.isPopupHovered.set(this.contains(this.el.nativeElement, relatedTarget));
    }

    enterTooltip(): void {
        this.isPopupHovered.set(true);
    }

    exitTooltip(event: MouseEvent): void {
        this.isPopupHovered.set(false);
        this.isOriginalImageHovered.set(this.contains(this.originalImageElement, event.relatedTarget));
    }

    private contains(container: HTMLElement | null, target: EventTarget | null): boolean {
        return container !== null && target instanceof Node && container.contains(target);
    }
}
